import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  Card,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Textarea,
  useToast,
} from '@biartbt/biartd'
import type {
  FormMasterItem,
  FormMasterCreateUpdatePayload,
  TableColumnDef,
} from '../api/formMasterApi'
import {
  formMasterApi,
  PARENT_MENU_OPTIONS,
  DATA_TYPE_OPTIONS,
  LIST_VIEW_OPTIONS,
} from '../api/formMasterApi'

type FormState = {
  entityName: string
  displayName: string
  description: string
  parentMenu: string
  menuName: string
  tableColumns: TableColumnDef[]
  exampleHeaders: string
  exampleData: string
  listViewType: string
  hasImport: boolean
  hasExport: boolean
  filterType: string
  hasRefresh: boolean
  hasCreate: boolean
  pageSize: number
  backendRoute: string
  frontendRoute: string
  responseFormat: string
  responseDataPath: string
  responsePaginationPath: string
  responseNotes: string
}

const emptyForm: FormState = {
  entityName: '',
  displayName: '',
  description: '',
  parentMenu: 'system',
  menuName: '',
  tableColumns: [],
  exampleHeaders: '',
  exampleData: '',
  listViewType: 'table',
  hasImport: true,
  hasExport: true,
  filterType: 'column',
  hasRefresh: true,
  hasCreate: true,
  pageSize: 10,
  backendRoute: '',
  frontendRoute: '',
  responseFormat: 'standard',
  responseDataPath: '',
  responsePaginationPath: '',
  responseNotes: '',
}

function toKebab(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase()
}

function detectDataType(value: string): TableColumnDef['dataType'] {
  if (!value || value.trim() === '') return 'text'
  const trimmed = value.trim().toLowerCase()
  if (trimmed === 'true' || trimmed === 'false' || trimmed === 'evet' || trimmed === 'hayır' || trimmed === 'aktif' || trimmed === 'pasif')
    return 'boolean'
  if (!isNaN(Number(trimmed)) && trimmed !== '') return 'number'
  if (/^\d{4}[-/]\d{2}[-/]\d{2}/.test(trimmed) || /^\d{2}[./]\d{2}[./]\d{4}/.test(trimmed))
    return 'date'
  return 'text'
}

function parseColumns(headers: string, dataRows: string): TableColumnDef[] {
  const headerList = headers.split(',').map((h) => h.trim()).filter(Boolean)
  if (headerList.length === 0) return []
  const rows = dataRows.split('\n').map((r) => r.trim()).filter(Boolean)
  const firstRow = rows.length > 0 ? rows[0].split(',').map((v) => v.trim()) : []
  return headerList.map((header, idx) => ({
    fieldName: toKebab(header),
    dataType: detectDataType(firstRow[idx] ?? ''),
    showInTable: true,
    label: header,
    showInForm: true,
    required: false,
    width: 150,
    sortOrder: idx + 1,
  }))
}

function safeParseJson<T>(json: string, fallback: T): T {
  try { return JSON.parse(json) as T } catch { return fallback }
}

function safeParseJsonToString(json: string): string {
  try {
    const parsed = JSON.parse(json)
    if (Array.isArray(parsed)) return parsed.join(', ')
    return String(parsed)
  } catch { return json }
}

function buildPayload(f: FormState): FormMasterCreateUpdatePayload {
  return {
    entityName: f.entityName.trim(),
    displayName: f.displayName.trim(),
    description: f.description.trim() || undefined,
    parentMenu: f.parentMenu,
    menuName: f.menuName.trim(),
    tableColumns: JSON.stringify(f.tableColumns),
    exampleData: f.exampleData.trim() ? JSON.stringify(f.exampleData.split('\n').filter(Boolean)) : '[]',
    exampleHeaders: f.exampleHeaders.trim() ? JSON.stringify(f.exampleHeaders.split(',').map((h) => h.trim()).filter(Boolean)) : '[]',
    listViewType: f.listViewType,
    hasImport: f.hasImport,
    hasExport: f.hasExport,
    filterType: f.filterType,
    hasRefresh: f.hasRefresh,
    hasCreate: f.hasCreate,
    pageSize: f.pageSize,
    backendRoute: f.backendRoute.trim(),
    frontendRoute: f.frontendRoute.trim(),
    responseFormat: f.responseFormat,
    responseDataPath: f.responseDataPath.trim() || undefined,
    responsePaginationPath: f.responsePaginationPath.trim() || undefined,
    responseNotes: f.responseNotes.trim() || undefined,
  }
}

export default function FormMasterFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()

  const isEdit = !!id
  const [form, setForm] = useState<FormState>(emptyForm)
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(isEdit)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Load existing definition for edit
  useEffect(() => {
    if (!id) return
    setLoading(true)
    formMasterApi
      .getById(Number(id))
      .then((item: FormMasterItem) => {
        setForm({
          entityName: item.entityName,
          displayName: item.displayName,
          description: item.description ?? '',
          parentMenu: item.parentMenu,
          menuName: item.menuName,
          tableColumns: safeParseJson<TableColumnDef[]>(item.tableColumns, []),
          exampleHeaders: safeParseJsonToString(item.exampleHeaders),
          exampleData: safeParseJsonToString(item.exampleData),
          listViewType: item.listViewType,
          hasImport: item.hasImport,
          hasExport: item.hasExport,
          filterType: item.filterType,
          hasRefresh: item.hasRefresh,
          hasCreate: item.hasCreate,
          pageSize: item.pageSize,
          backendRoute: item.backendRoute,
          frontendRoute: item.frontendRoute,
          responseFormat: item.responseFormat,
          responseDataPath: item.responseDataPath ?? '',
          responsePaginationPath: item.responsePaginationPath ?? '',
          responseNotes: item.responseNotes ?? '',
        })
      })
      .catch((e: any) => setLoadError(e?.message ?? t('formMaster.toast.loadFailed')))
      .finally(() => setLoading(false))
  }, [id, t])

  const errors: Record<string, string | undefined> = {
    entityName: !form.entityName.trim() ? t('formMaster.validation.entityNameRequired') : undefined,
    displayName: !form.displayName.trim() ? t('formMaster.validation.displayNameRequired') : undefined,
    menuName: !form.menuName.trim() ? t('formMaster.validation.menuNameRequired') : undefined,
    backendRoute: !form.backendRoute.trim() ? t('formMaster.validation.backendRouteRequired') : undefined,
    frontendRoute: !form.frontendRoute.trim() ? t('formMaster.validation.frontendRouteRequired') : undefined,
  }
  const hasErrors = Object.values(errors).some(Boolean)

  const onSave = async () => {
    setTouched(true)
    if (hasErrors) return

    setSaving(true)
    try {
      const payload = buildPayload(form)
      if (isEdit) {
        await formMasterApi.update(Number(id), payload)
        toast({ title: t('formMaster.toast.saved'), description: t('formMaster.toast.updated'), variant: 'success' })
      } else {
        const res = await formMasterApi.create(payload)
        toast({ title: t('formMaster.toast.saved'), description: t('formMaster.toast.created'), variant: 'success' })
        navigate(`/system/form-master/${res.id}/edit`, { replace: true })
        return
      }
    } catch (e: any) {
      toast({ title: t('formMaster.toast.error'), description: e?.message ?? t('formMaster.toast.saveFailed'), variant: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const handleEntityNameChange = (value: string) => {
    const clean = value.replace(/[^a-zA-Z0-9]/g, '')
    setForm((s) => ({
      ...s,
      entityName: clean,
      backendRoute: s.backendRoute || `/${toKebab(clean)}`,
      frontendRoute: s.frontendRoute || `/system/${toKebab(clean)}`,
    }))
  }

  const generateColumns = () => {
    if (!form.exampleHeaders.trim()) return
    const cols = parseColumns(form.exampleHeaders, form.exampleData)
    setForm((s) => ({ ...s, tableColumns: cols }))
  }

  const updateColumn = (idx: number, patch: Partial<TableColumnDef>) => {
    setForm((s) => ({
      ...s,
      tableColumns: s.tableColumns.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }))
  }

  const removeColumn = (idx: number) => {
    setForm((s) => ({ ...s, tableColumns: s.tableColumns.filter((_, i) => i !== idx) }))
  }

  const addColumn = () => {
    setForm((s) => ({
      ...s,
      tableColumns: [
        ...s.tableColumns,
        { fieldName: '', dataType: 'text' as const, showInTable: true, label: '', showInForm: true, required: false, width: 150, sortOrder: s.tableColumns.length + 1 },
      ],
    }))
  }

  if (loading) {
    return (
      <div className="p-[25px]">
        <Skeleton heightClassName="h-10" className="mb-4" />
        <Skeleton heightClassName="h-[400px]" />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="p-[25px]">
        <Alert variant="danger" type="default">{loadError}</Alert>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/system/form-master')}>
          {t('formMaster.actions.backToList')}
        </Button>
      </div>
    )
  }

  return (
    <div className="p-[25px]">
      <PageHeader
        title={isEdit ? t('formMaster.modal.edit') : t('formMaster.modal.create')}
        subtitle={isEdit ? form.displayName : t('formMaster.subtitle')}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate('/system/form-master')} disabled={saving}>
              {t('formMaster.actions.backToList')}
            </Button>
            <Button onClick={onSave} loading={saving} disabled={saving}>
              {t('formMaster.actions.save')}
            </Button>
          </>
        }
      />

      <Card className="mt-4 p-6">
        <div className="grid gap-6">
          {/* Section 1: General */}
          <fieldset className="rounded-lg border p-4">
            <legend className="px-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('formMaster.sections.general')}
            </legend>
            <div className="grid gap-4">
              <Input
                label={t('formMaster.fields.displayName')}
                value={form.displayName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, displayName: e.target.value }))}
                onBlur={() => setTouched(true)}
                error={touched && !!errors.displayName}
                helperText={touched ? errors.displayName : undefined}
                required
                disabled={saving}
                placeholder={t('formMaster.fields.displayNamePlaceholder')}
              />
              <Textarea
                label={t('formMaster.fields.description')}
                value={form.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm((s) => ({ ...s, description: e.target.value }))}
                disabled={saving}
                placeholder={t('formMaster.fields.descriptionPlaceholder')}
                rows={2}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {t('formMaster.fields.parentMenu')}
                  </label>
                  <Select
                    value={form.parentMenu}
                    onValueChange={(v: string) => setForm((s) => ({ ...s, parentMenu: v }))}
                    disabled={saving}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('formMaster.fields.parentMenuPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {PARENT_MENU_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{t(opt.label)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  label={t('formMaster.fields.menuName')}
                  value={form.menuName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, menuName: e.target.value }))}
                  onBlur={() => setTouched(true)}
                  error={touched && !!errors.menuName}
                  helperText={touched ? errors.menuName : undefined}
                  required
                  disabled={saving}
                  placeholder={t('formMaster.fields.menuNamePlaceholder')}
                />
              </div>
            </div>
          </fieldset>

          {/* Section 2: Data / Entity */}
          <fieldset className="rounded-lg border p-4">
            <legend className="px-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('formMaster.sections.data')}
            </legend>
            <div className="grid gap-4">
              <Input
                label={t('formMaster.fields.entityName')}
                value={form.entityName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEntityNameChange(e.target.value)}
                onBlur={() => setTouched(true)}
                error={touched && !!errors.entityName}
                helperText={touched ? errors.entityName : undefined}
                required
                disabled={saving}
                placeholder={t('formMaster.fields.entityNamePlaceholder')}
              />
              <Input
                label={t('formMaster.fields.exampleHeaders')}
                value={form.exampleHeaders}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, exampleHeaders: e.target.value }))}
                disabled={saving}
                placeholder={t('formMaster.fields.exampleHeadersPlaceholder')}
              />
              <Textarea
                label={t('formMaster.fields.exampleData')}
                value={form.exampleData}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm((s) => ({ ...s, exampleData: e.target.value }))}
                disabled={saving}
                placeholder={t('formMaster.fields.exampleDataPlaceholder')}
                rows={3}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={generateColumns}
                disabled={saving || !form.exampleHeaders.trim()}
              >
                {t('formMaster.actions.generateColumns')}
              </Button>
            </div>
          </fieldset>

          {/* Section 3: Columns */}
          <fieldset className="rounded-lg border p-4">
            <legend className="px-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('formMaster.sections.columns')} ({form.tableColumns.length})
            </legend>
            {form.tableColumns.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('formMaster.noColumns')}</p>
            ) : (
              <div className="space-y-3">
                {form.tableColumns.map((col, idx) => (
                  <div key={idx} className="flex flex-wrap items-end gap-2 rounded border p-3">
                    <div className="w-[140px]">
                      <Input
                        label={idx === 0 ? t('formMaster.colFields.fieldName') : undefined}
                        value={col.fieldName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateColumn(idx, { fieldName: e.target.value })}
                        disabled={saving}
                        placeholder="fieldName"
                      />
                    </div>
                    <div className="w-[140px]">
                      <Input
                        label={idx === 0 ? t('formMaster.colFields.label') : undefined}
                        value={col.label}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateColumn(idx, { label: e.target.value })}
                        disabled={saving}
                        placeholder="Label"
                      />
                    </div>
                    <div className="w-[110px]">
                      {idx === 0 && (
                        <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">
                          {t('formMaster.colFields.dataType')}
                        </label>
                      )}
                      <Select
                        value={col.dataType}
                        onValueChange={(v: string) => updateColumn(idx, { dataType: v as TableColumnDef['dataType'] })}
                        disabled={saving}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DATA_TYPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{t(opt.label)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-3 pt-1">
                      <Switch
                        checked={col.showInTable}
                        onCheckedChange={(v: boolean) => updateColumn(idx, { showInTable: v })}
                        label={t('formMaster.colFields.tableShort')}
                        disabled={saving}
                      />
                      <Switch
                        checked={col.showInForm}
                        onCheckedChange={(v: boolean) => updateColumn(idx, { showInForm: v })}
                        label={t('formMaster.colFields.formShort')}
                        disabled={saving}
                      />
                      <Switch
                        checked={col.required}
                        onCheckedChange={(v: boolean) => updateColumn(idx, { required: v })}
                        label={t('formMaster.colFields.reqShort')}
                        disabled={saving}
                      />
                    </div>
                    <div className="w-[70px]">
                      <Input
                        label={idx === 0 ? t('formMaster.colFields.width') : undefined}
                        type="number"
                        value={String(col.width)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateColumn(idx, { width: parseInt(e.target.value, 10) || 150 })}
                        disabled={saving}
                      />
                    </div>
                    <div className="w-[70px]">
                      <Input
                        label={idx === 0 ? t('formMaster.colFields.sortOrder') : undefined}
                        type="number"
                        value={String(col.sortOrder)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateColumn(idx, { sortOrder: parseInt(e.target.value, 10) || 0 })}
                        disabled={saving}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => removeColumn(idx)}
                      disabled={saving}
                      className="mt-auto"
                    >
                      <i className="material-symbols-outlined text-sm">delete</i>
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <Button type="button" variant="outline" size="sm" onClick={addColumn} disabled={saving} className="mt-3">
              <i className="material-symbols-outlined text-sm mr-1">add</i>
              {t('formMaster.actions.addColumn')}
            </Button>
          </fieldset>

          {/* Section 4: Options */}
          <fieldset className="rounded-lg border p-4">
            <legend className="px-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('formMaster.sections.options')}
            </legend>
            <div className="grid gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {t('formMaster.fields.listViewType')}
                  </label>
                  <Select
                    value={form.listViewType}
                    onValueChange={(v: string) => setForm((s) => ({ ...s, listViewType: v }))}
                    disabled={saving}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LIST_VIEW_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{t(opt.label)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  label={t('formMaster.fields.pageSize')}
                  type="number"
                  value={String(form.pageSize)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, pageSize: parseInt(e.target.value, 10) || 10 }))}
                  disabled={saving}
                  placeholder="10"
                />
              </div>
              <div className="flex flex-wrap gap-6">
                <Switch checked={form.hasImport} onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, hasImport: v }))} label={t('formMaster.fields.hasImport')} disabled={saving} />
                <Switch checked={form.hasExport} onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, hasExport: v }))} label={t('formMaster.fields.hasExport')} disabled={saving} />
                <Switch checked={form.hasRefresh} onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, hasRefresh: v }))} label={t('formMaster.fields.hasRefresh')} disabled={saving} />
                <Switch checked={form.hasCreate} onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, hasCreate: v }))} label={t('formMaster.fields.hasCreate')} disabled={saving} />
              </div>
            </div>
          </fieldset>

          {/* Section 5: Routes */}
          <fieldset className="rounded-lg border p-4">
            <legend className="px-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('formMaster.sections.routes')}
            </legend>
            <div className="grid gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t('formMaster.fields.backendRoute')}
                  value={form.backendRoute}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, backendRoute: e.target.value }))}
                  onBlur={() => setTouched(true)}
                  error={touched && !!errors.backendRoute}
                  helperText={touched ? errors.backendRoute : undefined}
                  required
                  disabled={saving}
                  placeholder="/entity-name"
                />
                <Input
                  label={t('formMaster.fields.frontendRoute')}
                  value={form.frontendRoute}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, frontendRoute: e.target.value }))}
                  onBlur={() => setTouched(true)}
                  error={touched && !!errors.frontendRoute}
                  helperText={touched ? errors.frontendRoute : undefined}
                  required
                  disabled={saving}
                  placeholder="/system/entity-name"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {t('formMaster.fields.responseFormat')}
                </label>
                <Select
                  value={form.responseFormat}
                  onValueChange={(v: string) => setForm((s) => ({ ...s, responseFormat: v }))}
                  disabled={saving}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">{t('formMaster.responseFormats.standard')}</SelectItem>
                    <SelectItem value="custom">{t('formMaster.responseFormats.custom')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.responseFormat === 'custom' && (
                <div className="grid gap-4">
                  <Input
                    label={t('formMaster.fields.responseDataPath')}
                    value={form.responseDataPath}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, responseDataPath: e.target.value }))}
                    disabled={saving}
                    placeholder="data.items"
                  />
                  <Input
                    label={t('formMaster.fields.responsePaginationPath')}
                    value={form.responsePaginationPath}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, responsePaginationPath: e.target.value }))}
                    disabled={saving}
                    placeholder="data.pagination"
                  />
                  <Textarea
                    label={t('formMaster.fields.responseNotes')}
                    value={form.responseNotes}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm((s) => ({ ...s, responseNotes: e.target.value }))}
                    disabled={saving}
                    placeholder={t('formMaster.fields.responseNotesPlaceholder')}
                    rows={2}
                  />
                </div>
              )}
            </div>
          </fieldset>
        </div>
      </Card>
    </div>
  )
}
